import { Injectable, OnDestroy } from '@angular/core';

declare var google: any;

export interface Language {
  code: string;
  name: string;
  flag: string;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService implements OnDestroy {
  private bannerCheckInterval?: any;

  languages: Language[] = [
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'ga', name: 'Gaeilge', flag: 'ie' },
    { code: 'es', name: 'Español', flag: 'es' },
    { code: 'fr', name: 'Français', flag: 'fr' },
    { code: 'ur', name: 'اردو', flag: 'pk' },
    { code: 'ar', name: 'العربية', flag: 'sa' },
    { code: 'de', name: 'Deutsch', flag: 'de' },
    { code: 'it', name: 'Italiano', flag: 'it' },
    { code: 'pt', name: 'Português', flag: 'pt' },
    { code: 'pl', name: 'Polski', flag: 'pl' },
    { code: 'zh', name: '中文', flag: 'cn' },
    { code: 'ru', name: 'Русский', flag: 'ru' },
    { code: 'nl', name: 'Nederlands', flag: 'nl' },
    { code: 'sv', name: 'Svenska', flag: 'se' },
    { code: 'da', name: 'Dansk', flag: 'dk' },
    { code: 'no', name: 'Norsk', flag: 'no' },
  ];

  constructor() {
    this.initBannerHiding();
  }

  // Get current language from sessionStorage ONLY
  getCurrentLanguage(): string {
    try {
      const savedLanguage = sessionStorage.getItem('selectedLanguage');
      if (savedLanguage && this.languages.some((lang) => lang.code === savedLanguage)) {
        console.log('Found saved language in sessionStorage:', savedLanguage);
        return savedLanguage;
      }
    } catch (error) {
      console.error('Error loading saved language:', error);
    }

    console.log('No saved language found, using default: en');
    return 'en';
  }

  // Dynamic method to clear ALL Google Translate cookies for any domain
  private clearAllLanguageData(): void {
    try {
      console.log('=== Clearing ALL language data ===');

      // Clear sessionStorage
      sessionStorage.removeItem('selectedLanguage');
      sessionStorage.removeItem('googtrans');

      // Clear localStorage
      localStorage.removeItem('selectedLanguage');
      localStorage.removeItem('googtrans');

      // Get current hostname dynamically
      const hostname = window.location.hostname;
      const isHTTPS = window.location.protocol === 'https:';

      console.log('Current hostname:', hostname);
      console.log('Is HTTPS:', isHTTPS);

      // Build dynamic domain list based on current hostname
      const domains = this.buildDomainList(hostname);

      // Language paths for cookie clearing
      const paths = [
        '/',
        '/en',
        '/ga',
        '/es',
        '/fr',
        '/ur',
        '/ar',
        '/de',
        '/it',
        '/pt',
        '/pl',
        '/zh',
        '/ru',
        '/nl',
        '/sv',
        '/da',
        '/no',
      ];

      console.log('Clearing cookies for domains:', domains);

      // Clear cookies for all domain/path combinations
      domains.forEach((domain) => {
        paths.forEach((path) => {
          if (isHTTPS) {
            // HTTPS - add secure flag
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; secure; samesite=lax`;
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; secure; samesite=lax`;
          } else {
            // HTTP - no secure flag
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
          }
        });
      });

      // Additional cleanup for any remaining googtrans cookies
      const allCookies = document.cookie.split(';');
      allCookies.forEach((cookie) => {
        const [name] = cookie.trim().split('=');
        if (name === 'googtrans') {
          domains.forEach((domain) => {
            paths.forEach((path) => {
              if (isHTTPS) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; secure; samesite=lax`;
              } else {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`;
              }
            });
          });
        }
      });

      // Clear URL hash
      if (window.location.hash.includes('googtrans')) {
        window.location.hash = '';
      }

      console.log('All language data cleared for hostname:', hostname);
    } catch (error) {
      console.error('Error clearing language data:', error);
    }
  }

  // Build domain list dynamically based on current hostname
  private buildDomainList(hostname: string): string[] {
    const domains = [hostname];

    // Add dot-prefixed version for subdomain support
    if (!hostname.startsWith('.')) {
      domains.push(`.${hostname}`);
    }

    // If it's a subdomain, add root domain variations
    const parts = hostname.split('.');
    if (parts.length > 2) {
      // Extract root domain (last two parts)
      const rootDomain = parts.slice(-2).join('.');
      domains.push(rootDomain);
      domains.push(`.${rootDomain}`);
    }

    // Add common localhost variations for development
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      domains.push('localhost', '.localhost', '127.0.0.1');
    }

    // Remove duplicates
    return [...new Set(domains)];
  }

  // Save language to sessionStorage
  private saveLanguage(languageCode: string): void {
    try {
      sessionStorage.setItem('selectedLanguage', languageCode);
      console.log('Language saved to sessionStorage:', languageCode);
    } catch (error) {
      console.error('Error saving language:', error);
    }
  }

  getCurrentLanguageObject(): Language | undefined {
    return this.getLanguageByCode(this.getCurrentLanguage());
  }

  getLanguageByCode(code: string): Language | undefined {
    return this.languages.find((lang) => lang.code === code);
  }

  // Dynamic change language for any environment
  changeLanguage(languageCode: string): void {
    console.log('=== Changing language ===');
    console.log('From:', this.getCurrentLanguage());
    console.log('To:', languageCode);
    console.log('Current URL:', window.location.href);

    // Step 1: Clear ALL language data
    this.clearAllLanguageData();

    // Step 2: Wait for clearing to complete, then set new language
    setTimeout(() => {
      // Save the new language to sessionStorage
      this.saveLanguage(languageCode);

      // Step 3: Apply Google Translate
      if (languageCode === 'en') {
        console.log('Switching to English - reloading page');
        window.location.reload();
      } else {
        this.setTranslation(languageCode);
      }
    }, 300); // Increased delay for production
  }

  // Dynamic translation setup for any domain
  private setTranslation(languageCode: string): void {
    console.log('Setting up Google Translate for:', languageCode);

    const cookieValue = `/en/${languageCode}`;
    const hostname = window.location.hostname;
    const isHTTPS = window.location.protocol === 'https:';
    const domains = this.buildDomainList(hostname);

    console.log('Cookie value:', cookieValue);
    console.log('Setting for domains:', domains);

    // Set cookies for all relevant domains
    domains.forEach((domain) => {
      if (isHTTPS) {
        document.cookie = `googtrans=${cookieValue}; path=/; domain=${domain}; secure; samesite=lax`;
      } else {
        document.cookie = `googtrans=${cookieValue}; path=/; domain=${domain}`;
      }
    });

    // Also set without domain for current page
    if (isHTTPS) {
      document.cookie = `googtrans=${cookieValue}; path=/; secure; samesite=lax`;
    } else {
      document.cookie = `googtrans=${cookieValue}; path=/`;
    }

    // Set session storage for Google Translate
    sessionStorage.setItem('googtrans', cookieValue);

    // Set URL hash
    window.location.hash = `#googtrans(en|${languageCode})`;

    console.log(`Google Translate configured for ${languageCode}, reloading page`);

    // Reload the page to apply translation
    window.location.reload();
  }

  // Alternative method without page reload
  changeLanguageNoReload(languageCode: string): void {
    console.log('Changing language without reload to:', languageCode);

    this.clearAllLanguageData();

    setTimeout(() => {
      this.saveLanguage(languageCode);
      this.waitAndTryTranslation(languageCode, 0);
    }, 300);
  }

  private initBannerHiding(): void {
    this.bannerCheckInterval = setInterval(() => {
      this.hideBanner();
    }, 200);
  }

  private hideBanner(): void {
    const bannerSelectors = [
      '.goog-te-banner-frame',
      '.skiptranslate',
      'iframe[src*="translate.google"]',
      '.goog-te-ftab-frame',
      '.goog-te-menu-frame',
      '.goog-te-balloon-frame',
    ];

    bannerSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.display = 'none';
        htmlElement.style.visibility = 'hidden';
        htmlElement.style.height = '0px';
      });
    });

    document.body.style.top = '0px';
    document.body.style.marginTop = '0px';
  }

  private waitAndTryTranslation(languageCode: string, attempts: number): void {
    const maxAttempts = 20;

    if (attempts >= maxAttempts) {
      console.log('Max attempts reached, using page reload method');
      this.setTranslation(languageCode);
      return;
    }

    if (typeof google !== 'undefined' && google.translate) {
      const selectElement = this.findGoogleTranslateSelect();
      if (selectElement && selectElement.options && selectElement.options.length > 0) {
        console.log('Found select element, changing language');
        selectElement.value = languageCode;
        selectElement.dispatchEvent(new Event('change'));
        setTimeout(() => this.hideBanner(), 500);
        return;
      }
    }

    setTimeout(() => {
      this.waitAndTryTranslation(languageCode, attempts + 1);
    }, 500);
  }

  private findGoogleTranslateSelect(): HTMLSelectElement | null {
    const selectors = [
      '.goog-te-combo',
      'select.goog-te-combo',
      '#google_translate_element select',
      '.goog-te-gadget select',
      '.goog-te-gadget-simple select',
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector) as HTMLSelectElement;
      if (element && element.options && element.options.length > 0) {
        console.log(`Found working select with selector: ${selector}`);
        return element;
      }
    }

    return null;
  }

  ngOnDestroy(): void {
    if (this.bannerCheckInterval) {
      clearInterval(this.bannerCheckInterval);
    }
  }
}
